import { Component, EventEmitter, Input, Output } from '@angular/core';
import { fade, openClose } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import { Encumbrance, EncumbranceType } from '@models/encumbrance';
import {
	FinancialInstitutionLender,
	Mortgagee,
	MortgageeType,
} from '@models/lender';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InstitutionRepo } from '@repos/institution/institution.service';
import { cloneDeep } from 'lodash';
import LogRocket from 'logrocket';
import { take } from 'rxjs/operators';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';

const MortgageTypeOptions: RadioGroupItem<MortgageeType>[] = [
	{ label: 'Financial institution', value: MortgageeType.INSTITUTION },
	{ label: 'Computershare', value: MortgageeType.COMPUTERSHARE },
	{ label: 'Private lender', value: MortgageeType.PRIVATE_LENDER },
	{ label: 'Other', value: MortgageeType.OTHER },
];

const computerShareInstitutionId = 'qnMR3Wv';

@UntilDestroy()
@Component({
	selector: 'app-mortgagee',
	templateUrl: './mortgagee.component.html',
	animations: [openClose(), fade()],
	standalone: false,
})
export class EncumbranceMortgageeComponent {
	public mortgageeTypeOptions = MortgageTypeOptions;
	public mortgageeTypes = MortgageeType;
	public _mortgagee: Mortgagee;

	public pushChangesToGlobalData: boolean = false;
	public ICON = ICON;

	@Output() onMortgageeUpdated: EventEmitter<Mortgagee> =
		new EventEmitter<Mortgagee>();

	@Input() showDischargeFields: boolean;
	@Input() set mortgagee(mortgagee: Mortgagee) {
		this._mortgagee = cloneDeep(mortgagee) ?? {
			mortgageeType: MortgageeType.INSTITUTION,
		};

		// Avoid data push to global data if encumbrance is NOT of type mortgage and the mortgagee type is OTHER
		const mortgageTypes = [
			EncumbranceType.LIEN,
			EncumbranceType.NOTICE_OF_SECURITY_INTEREST,
		];

		if (!mortgageTypes.includes((this._mortgagee as Encumbrance).type)) {
			if (this._mortgagee.mortgageeType === MortgageeType.OTHER) {
				this.pushChangesToGlobalData = false;
			} else {
				this.pushChangesToGlobalData = true;
			}
		} else {
			this.pushChangesToGlobalData = true;
		}
	}

	@Input() showCorrespondence = true;

	constructor(private institutionRepo: InstitutionRepo) {}

	async setMortgageeType(value: MortgageeType): Promise<void> {
		this._mortgagee.mortgageeType = value;
		this._mortgagee.privateLender = null;
		this._mortgagee.financialInstitution = null;
		this._mortgagee.solicitor = null;

		// if mortgagee type is computer share set computershare as the institution
		if (value === MortgageeType.COMPUTERSHARE) {
			try {
				const _doc = await this.institutionRepo
					.Get(computerShareInstitutionId)
					.pipe(take(1), untilDestroyed(this))
					.toPromise();
				this._mortgagee.financialInstitution = {
					...(_doc.data() as FinancialInstitutionLender),
					id: computerShareInstitutionId,
					logoUrl: _doc.data().logoUrl ?? null,
					branch: null,
				};
			} catch (err) {
				LogRocket.error(
					'Unable to select financial institution to ComputerShare',
					err,
				);
				console.log(
					'Unable to select financial institution to ComputerShare',
					err,
				);
			}
		}

		this.onMortgageeUpdated.emit(this._mortgagee);
	}

	updateMortgagee(mortagee: Mortgagee): void {
		this.onMortgageeUpdated.emit(mortagee);
	}
}
